import { Component, OnInit, ViewContainerRef, effect, inject, signal, viewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { ProductPage, ProductService } from '../../../shared/services/product.service';
import { ComponentRegistry } from '../../components/component-lookup';
import { StateService } from '../../services/state.service';
import { InstitutionSelectorComponent } from '../../components/institution-selector/institution-selector.component';
import { ProductHeaderComponent } from '../../components/product-header/product-header.component';
import { LegendComponent } from '../../components/legend/legend.component';
import { DecayMetricSelectorComponent } from '../../components/decay-metric-selector/decay-metric-selector.component';
import { PerformanceLegendComponent } from '../../components/performance-legend/performance-legend.component';
import { FlexRowComponent } from '../flex-row/flex-row.component';
import { FlexItemComponent } from '../flex-item/flex-item.component';
import { FlexCardContainerComponent } from '../flex-card-container/flex-card-container.component';
import { FlexCardComponent } from '../flex-card/flex-card.component';
import { TrendComponent } from '../../visuals/trend/trend.component';
import { QuarterlyGroupComponent } from '../../page-components/quarterly-group/quarterly-group.component';
import { QuarterlyInstitutionComponent } from '../../page-components/quarterly-institution/quarterly-institution.component';
import { CorrelationComponent } from '../../visuals/correlation/correlation.component';
import { RegressionComponent } from '../../visuals/regression/regression.component';
import { HeatMapComponent } from '../../visuals/heat-map/heat-map.component';
import { DecayBasicComponent } from '../../visuals/decay-basic/decay-basic.component';
import { HeatTableComponent } from '../../visuals/heat-table/heat-table.component';
import { DecayComponent } from '../../visuals/decay/decay.component';
import { PerformanceComponent } from '../../visuals/performance/performance.component';
import { FfiecManageGroupsComponent } from '../../page-components/ffiec/manage-groups/manage-groups.component';
import { FfiecCreateGroupComponent } from '../../page-components/ffiec/create-group/create-group.component';
import { SubjectHeaderComponent } from '../../visuals/subject-header/subject-header.component';
import { KpiCollectionComponent } from '../../visuals/kpi-collection/kpi-collection.component';
import { FlexContainerComponent } from '../flex-container/flex-container.component';
import { ScoreLegendComponent } from '../../visuals/score-legend/score-legend.component';
import { PieChartComponent } from '../../visuals/pie-chart/pie-chart.component';
import { BarLineStackedChartComponent } from '../../visuals/bar-line-stacked-chart/bar-line-stacked-chart.component';
import { BarLineConcentrationChartComponent } from '../../visuals/bar-line-concentration-chart/bar-line-concentration-chart.component';
import { KpiComponent } from '../../visuals/kpi/kpi.component';
import { FlexHeaderComponent } from '../flex-header/flex-header.component';
import { LineChartComponent } from '../../visuals/line-chart/line-chart.component';
import { NcuaManageGroupsComponent } from '../../page-components/ncua/manage-groups/manage-groups.component';
import { NcuaCreateGroupComponent } from '../../page-components/ncua/create-group/create-group.component';
import { BarHorizontalChartComponent } from '../../visuals/bar-horizontal-chart/bar-horizontal-chart.component';
import { BarNetChartComponent } from '../../visuals/bar-net-chart/bar-net-chart.component';
import { FeedbackDialogComponent } from '../../components/feedback-dialog/feedback-dialog.component';
import { BarLineClusteredChartComponent } from '../../visuals/bar-line-clustered-chart/bar-line-clustered-chart.component';
import { TextBoxComponent } from '../../visuals/text-box/text-box.component';
import { UserService } from '../../../shared/services/user.service';
import { ToggleComponent } from '../../components/toggle/toggle.component'; 
import { NmdLineChartComponent } from '../../visuals/nmd-line-chart/nmd-line-chart.component';


@Component({
  selector: 'app-product-page',
  standalone: true,
  imports: [InstitutionSelectorComponent, ProductHeaderComponent, LegendComponent, PerformanceLegendComponent,
    FeedbackDialogComponent,
    FlexContainerComponent, FlexRowComponent, FlexHeaderComponent, FlexItemComponent,
    FlexCardContainerComponent, FlexCardComponent,
    SubjectHeaderComponent,
    KpiCollectionComponent,
    KpiComponent,
    ScoreLegendComponent,
    PieChartComponent,
    BarLineStackedChartComponent,
    BarLineConcentrationChartComponent,
    LineChartComponent,
    BarHorizontalChartComponent,
    BarNetChartComponent,
    BarLineClusteredChartComponent,
    TextBoxComponent,
    TrendComponent,
    QuarterlyGroupComponent, QuarterlyInstitutionComponent,
    CorrelationComponent,
    PerformanceComponent,
    FfiecManageGroupsComponent, FfiecCreateGroupComponent,
    NcuaManageGroupsComponent, NcuaCreateGroupComponent, RegressionComponent,
    HeatMapComponent,HeatTableComponent, DecayComponent, DecayMetricSelectorComponent
    ,DecayBasicComponent,ToggleComponent,NmdLineChartComponent],
  templateUrl: './product-page.component.html',
  styleUrl: './product-page.component.scss',
  providers: []
})
export class ProductPageComponent implements OnInit {
  institutionSelector = viewChild.required('institutionSelector', { read: ViewContainerRef });
  productHeader = viewChild.required('productHeader', { read: ViewContainerRef });
  flexContainer = viewChild.required('flexContainer', { read: ViewContainerRef });
  legend = viewChild.required('legend', { read: ViewContainerRef });

  private route = inject(ActivatedRoute);
  private productService = inject(ProductService);
  private stateService = inject(StateService);

  readonly userProducts = inject(UserService).userProducts;
  readonly initialized = signal<boolean>(false);

  productPage: ProductPage = {} as ProductPage;
  paywall: boolean = false;

  constructor() {
    effect(() => {
      const initialized = this.initialized();
      const productsFetched = this.productService.productsFetched();
      const institution = this.stateService.institution();
      if (initialized && productsFetched && institution.institutionType) {
        this.route.url.subscribe(() => {
          const productPage = this.productService.resolveProductPage(this.route.snapshot);
          if (productPage) {
            this.productPage = productPage;
            this.setPaywall();
            this.buildPage();
          }
        });
      }
    });
  } 

  ngOnInit(): void {
    this.initialized.set(true);
  }

  private setPaywall(): void {
    if (this.productPage.tiers && this.productPage.tiers.length > 0) {
      this.paywall = !this.userProducts().some(p => this.productPage.tiers?.includes(p.tierClientCode));
    }
    else {
      this.paywall = false;
    }
  }

  private buildPage(): void {
   const currentProduct = this.productService.getCurrentProduct(this.route.snapshot);
    this.institutionSelector().clear();
    if (this.productPage.components[0].name) {
      const component = ComponentRegistry.get(this.productPage.components[0].name);
      if (component) {
        const componentRef = this.institutionSelector().createComponent(component);
        componentRef.instance.isLocked = this.productPage.components[0].data.isLocked;
      }
    }

    this.productHeader().clear();
    if (this.productPage.components[1].name) {
      const component = ComponentRegistry.get(this.productPage.components[1].name);
      if (component) {
        const componentRef = this.productHeader().createComponent(component);
        Object.keys(this.productPage.components[1].data).forEach(key => {
          componentRef.setInput(key, this.productPage.components[1].data[key]);
        });
        componentRef.setInput("currentProduct",currentProduct);
      }
    }

    this.flexContainer().clear();
    const pageComponent = this.productPage.components[2];
    if (pageComponent.name) {
      const component = ComponentRegistry.get(pageComponent.name);
      if (component) {
        const componentRef = this.flexContainer().createComponent(component);
        componentRef.setInput('paywall', this.paywall);
        componentRef.setInput('paywallMessage', this.productPage.paywallMessage);
        Object.keys(pageComponent.data).forEach(key => {
          componentRef.setInput(key, pageComponent.data[key]);
        });
        if (pageComponent.children && pageComponent.children.length > 0) {
          componentRef.setInput('children', pageComponent.children ?? []);
        }
      }
    }

    this.legend().clear();
    const legendComponent = this.productPage.components[3];
    if (legendComponent.name) {
      const component = ComponentRegistry.get(legendComponent.name);
      if (component) {
        const componentRef = this.legend().createComponent(component);
        Object.keys(legendComponent.data).forEach(key => {
          componentRef.setInput(key, legendComponent.data[key]);
        });
      }
    }
  }
}
